// /events/:eventId/edit — edit one event.
//
// Editing does NOT re-announce. The "announced" stamp is left alone server-side,
// so members are not notified twice about the same event. If that ever needs to
// change it becomes an explicit "notify members of the change" action, not a
// side effect of saving.
//
// A series occurrence can be edited here; only that one instance changes.

import { ApiClientError, fetchEvent, fetchGroup, updateEvent } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { GroupRole, type EventDetail, type GroupDetail } from '@msc/shared';
import { eventForm } from './eventForm.js';

export const eventEditView: View = async (outlet, ctx) => {
  const eventId = Number(ctx.params.eventId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(eventId) || eventId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid event.'));
    return;
  }

  let event: EventDetail;
  let group: GroupDetail;
  try {
    event = await fetchEvent(eventId);
    group = await fetchGroup(event.groupId);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.status === 404
          ? 'That event does not exist.'
          : 'Could not load this event.',
      ),
    );
    return;
  }

  if (event.myRole < GroupRole.EVENTS_MANAGER) {
    main.replaceChildren(
      el('p', { class: 'form-error' }, 'You need to be an Events Manager to edit events.'),
      el('p', {}, [el('a', { href: `/events/${eventId}` }, 'Back to the event')]),
    );
    return;
  }

  const form = eventForm({
    event,
    groupTimeZone: group.timeZone,
    allowedEventTypes: group.allowedEventTypes,
    submitLabel: 'Save changes',
    action: `/events/${eventId}`,
    onSubmit: async (body) => {
      await updateEvent(eventId, body);
      navigate(`/events/${eventId}`, true);
    },
  });

  const notes: HTMLElement[] = [];
  if (event.seriesId !== null) {
    notes.push(
      el(
        'p',
        { class: 'notice' },
        'This is one occurrence of a repeating event. Saving changes only this occurrence.',
      ),
    );
  }
  if (event.closedAt) {
    notes.push(
      el(
        'p',
        { class: 'notice' },
        'This event is closed. Edits still save, but RSVPs stay frozen.',
      ),
    );
  }

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/events/${eventId}` }, 'Back to the event')]),
    el('h1', {}, `Edit ${event.title}`),
    ...notes,
    el('section', { class: 'card' }, [form]),
  );
};
